import { makeArticle, notWorkingRelated } from "./learnFactory";

export const pageAlternativeToAntidepressants = makeArticle({
  slug: "alternative-to-antidepressants",
  title: "Alternatives to Antidepressants | Neuro Wellness",
  description:
    "Alternatives to antidepressants: TMS, SAINT®, SPRAVATO®, psychotherapy, and lifestyle approaches. Learn non-medication options for depression treatment.",
  canonical: "/learn/alternative-to-antidepressants",
  h1: "What Are the Alternatives to Antidepressants?",
  breadcrumbLabel: "Alternative to Antidepressants",
  shortAnswer:
    "Alternatives to antidepressant medication include TMS, SAINT® accelerated TMS, SPRAVATO® (esketamine), psychotherapy, and lifestyle approaches. TMS and SAINT® use magnetic brain stimulation and introduce no chemical into the body. SPRAVATO® is an in-office nasal spray that works through a different mechanism than oral antidepressants. Psychotherapy (CBT, behavioral activation, interpersonal therapy) is an evidence-based non-medication option. For severe or treatment-resistant depression, these alternatives may be appropriate. The best choice depends on your clinical circumstances and should be discussed with a psychiatrist.",
  keyTakeaways: [
    "TMS and SAINT® are non-medication brain stimulation alternatives.",
    "SPRAVATO® works through a different mechanism than oral antidepressants.",
    "Psychotherapy is an evidence-based non-medication option.",
    "These alternatives are FDA-cleared or approved for depression.",
    "The best choice depends on clinical circumstances.",
  ],
  sections: [
    {
      heading: "TMS (Transcranial Magnetic Stimulation)",
      paragraphs: [
        "TMS is FDA-cleared for adults with major depressive disorder who haven't improved on antidepressant medication. It uses magnetic pulses to stimulate the left dorsolateral prefrontal cortex — a brain region involved in mood regulation. TMS introduces no chemical into the body and doesn't cause systemic side effects like weight gain or sexual dysfunction. A standard course is about 36 sessions over six weeks. TMS is broadly covered by insurance for treatment-resistant depression.",
      ],
    },
    {
      heading: "SAINT® Accelerated TMS",
      paragraphs: [
        "SAINT® is an accelerated TMS protocol — 50 sessions in five days with fMRI-guided targeting. Like TMS, it is non-invasive and introduces no chemical. SAINT® uses a personalized target chosen from each patient's own brain scan. In a sham-controlled trial, 79% of the active group reached remission. SAINT® may be appropriate for treatment-resistant depression when a faster timeline is desired.",
      ],
    },
    {
      heading: "SPRAVATO® (Esketamine)",
      paragraphs: [
        "SPRAVATO® is an FDA-approved nasal spray for treatment-resistant depression. It works through NMDA receptor modulation — a different mechanism than standard antidepressants (which target serotonin, norepinephrine, or dopamine). SPRAVATO® is administered in-office under monitoring and requires a driver home. It may be an alternative for patients who haven't responded to oral antidepressants.",
      ],
    },
    {
      heading: "Psychotherapy",
      paragraphs: [
        "Psychotherapy is an evidence-based non-medication treatment for depression. Types include cognitive behavioral therapy (CBT), behavioral activation, interpersonal therapy, and mindfulness-based approaches. For mild-to-moderate depression, therapy can be as effective as medication. For moderate-to-severe depression, combining therapy with medication or other treatments is often more effective. Therapy is frequently part of a TMS or SAINT® treatment plan.",
      ],
    },
    {
      heading: "Lifestyle Approaches",
      paragraphs: [
        "Lifestyle approaches — regular exercise, consistent sleep, daylight exposure, reduced alcohol, and social connection — have evidence for mild depressive symptoms. These are helpful additions but are rarely sufficient as stand-alone treatments for moderate-to-severe depression. They work best as part of a comprehensive treatment plan that may include therapy, medication, or brain stimulation treatments.",
      ],
    },
  ],
  faqs: [
    {
      question: "What are the alternatives to antidepressants?",
      answer:
        "Alternatives include TMS, SAINT® accelerated TMS, SPRAVATO® (esketamine), psychotherapy, and lifestyle approaches. TMS and SAINT® use magnetic brain stimulation with no chemical. SPRAVATO® is an in-office nasal spray. Psychotherapy is evidence-based for depression. The best choice depends on your circumstances.",
    },
    {
      question: "Is TMS an alternative to antidepressants?",
      answer:
        "Yes. TMS is FDA-cleared for adults whose depression hasn't improved on antidepressants. It uses magnetic pulses, introduces no chemical, and doesn't cause systemic side effects. TMS may be an alternative when antidepressants haven't worked or aren't desired.",
    },
    {
      question: "Can SAINT replace antidepressants?",
      answer:
        "SAINT® may be an option when antidepressants haven't worked, but it doesn't necessarily replace medication. Many patients continue medication during and after SAINT®. Whether to continue, adjust, or discontinue medication is a clinical decision. Never stop antidepressants abruptly without supervision.",
    },
    {
      question: "Is therapy an alternative to antidepressants?",
      answer:
        "For mild-to-moderate depression, therapy can be as effective as medication. For moderate-to-severe depression, combining therapy with medication or other treatments is often more effective. Therapy is an evidence-based non-medication option that can be used alone or alongside other treatments.",
    },
    {
      question: "Are there natural alternatives to antidepressants?",
      answer:
        "Exercise, regular sleep, daylight, reduced alcohol, and social connection have evidence for mild symptoms. St. John's wort has trial evidence for mild-to-moderate depression but interacts with many medications. No supplement is FDA-approved for depression. Discuss any supplement with your prescriber. For moderate-to-severe depression, professional treatment is recommended.",
    },
  ],
  sources: [
    {
      citation:
        "O'Reardon JP, et al. Efficacy and safety of TMS in the acute treatment of major depression. Biol Psychiatry. 2007;62(11):1208-1216.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17123640/",
    },
    {
      citation:
        "Cuijpers P, et al. Psychotherapy for depression: a meta-analysis. World Psychiatry. 2023;22(1):105-115.",
      url: "https://pubmed.ncbi.nlm.nih.gov/36640402/",
    },
  ],
  relatedLinks: notWorkingRelated,
});
